export function mmss(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

export function relativeTime(iso: string, now = Date.now()): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const diff = Math.max(0, now - t);
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} d ago`;
  return new Date(t).toLocaleDateString();
}

export function formatBytes(n: number | null | undefined): string {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** The iOS app tags uploads with its own id ("Memo [iOS E841...]"). People never need to see it. Display only. */
const DEVICE_TAG = /\s*\[(?:iOS|iPhone|iPad|Android)\s+[0-9A-Fa-f][0-9A-Fa-f-]*(?:\u2026|\.\.\.)?\]\s*$/;

/** A title people can read: the device tag is dropped, and an empty title says what it is. */
export function conversationTitle(title: string | null | undefined, fallback = 'Untitled conversation'): string {
  const plain = (title ?? '').replace(DEVICE_TAG, '').trim();
  return plain.length > 0 ? plain : fallback;
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "Today", "Yesterday", "Oct 8" (with the year when it is not this year). */
export function shortDate(iso: string, now = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = (x: Date): number => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  const base = `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}`;
  return d.getFullYear() === now.getFullYear() ? base : `${base}, ${d.getFullYear()}`;
}

/** "October 9" (with the year when it is not this year). */
export function longDate(iso: string, now = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const base = `${MONTHS_LONG[d.getMonth()]} ${d.getDate()}`;
  return d.getFullYear() === now.getFullYear() ? base : `${base}, ${d.getFullYear()}`;
}

/** "8 min" or "1 h 5 min" between two times; empty when the end is unknown. */
export function duration(startIso: string, endIso: string | null): string {
  if (!endIso) return '';
  const minutes = Math.max(1, Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000));
  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

/** Two-letter initials for avatar circles: first and last word, or a fallback. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const a = words[0]?.[0];
  const b = words.length > 1 ? words[words.length - 1]?.[0] : undefined;
  return ((a ?? '?') + (b ?? '')).toUpperCase();
}

/** Display a date for machine-generated meeting identifiers; preserve meaningful user titles. */
export function meetingTitle(title: string | null | undefined, startedAt: string): string {
  const value = title?.trim() ?? '';
  const uuid = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
  if (value && !new RegExp(`^(?:meeting[\\s:_-]*)?${uuid}$`, 'i').test(value)) return value;
  const date = new Date(startedAt);
  return Number.isNaN(date.getTime()) ? 'Meeting' : `Meeting · ${date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`;
}
